import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CloseIcon from "@material-ui/icons/Close";
import { RetentionConflictDetails } from "@dashboard-v3/api";
import ConflictUsage from "./ConflictUsage";
import { formatDays, getConflictRoute } from "./helpers";
import { RetentionConflictOrigin } from "./types";

export { default as getRetentionConflictDetails } from "./getRetentionConflictDetails";
export type { RetentionConflictOrigin } from "./types";

interface Props {
  open: boolean;
  onClose: () => void;
  origin: RetentionConflictOrigin;
  details: RetentionConflictDetails;
  savingId?: string;
}

const SHARES_SETTINGS_ROUTE = "/shares-settings";

export default function RetentionConflictModal(props: Props) {
  const { open, onClose, origin, details, savingId } = props;
  const { conflicts, linkDays, retentionDays } = details;
  const { t } = useTranslation("components");
  const classes = useStyles();
  const showOrganizationLink = origin === "rule";
  const keys = `retentionConflictModal.${origin}`;
  const days = origin === "organization" ? retentionDays : linkDays;
  const neverSuffix = days === null ? "Never" : "";
  const textOptions = {
    count: conflicts.length,
    days: formatDays(days, t),
    retentionDays: formatDays(retentionDays, t),
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.paper }}
    >
      <DialogTitle disableTypography className={classes.header}>
        <IconButton
          className={classes.closeButton}
          size="small"
          aria-label={t("common:close")}
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography className={classes.title} variant="h6">
          <ErrorOutlineIcon className={classes.titleIcon} />
          <span>{t(`${keys}.title${neverSuffix}`, textOptions)}</span>
        </Typography>
        <Typography
          className={classes.subtitle}
          variant="body2"
          color="textSecondary"
        >
          {t(`${keys}.whatToDo`, textOptions)}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.message}>
        <Typography variant="body2" role="alert">
          <Trans
            i18nKey={`${keys}.message${neverSuffix}`}
            t={t}
            values={textOptions}
            components={{ b: <b className={classes.strong} /> }}
          />
        </Typography>
      </DialogContent>

      <DialogContent>
        <Typography className={classes.sectionLabel} color="textSecondary">
          {t(`${keys}.list`)}
        </Typography>
        <List dense disablePadding className={classes.list}>
          {conflicts.map(conflict => (
            <ListItem
              key={conflict.id}
              className={classes.listItem}
              divider
              button={false as any}
            >
              <Link
                className={classes.link}
                to={getConflictRoute(conflict)}
                target="_blank"
              >
                <Tooltip title={conflict.name}>
                  <span className={classes.name}>{conflict.name}</span>
                </Tooltip>
                <OpenInNewIcon color="primary" fontSize="inherit" />
              </Link>
              <Chip
                size="small"
                label={t(`${keys}.chip`, {
                  days: formatDays(conflict.days, t),
                  retentionDays: formatDays(conflict.retentionDays, t),
                })}
              />
              <Typography
                className={classes.usage}
                variant="caption"
                color="textSecondary"
                noWrap
              >
                <ConflictUsage
                  origin={origin}
                  conflict={conflict}
                  savingId={savingId}
                  retentionDays={retentionDays}
                />
              </Typography>
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        {showOrganizationLink && (
          <Button
            href={SHARES_SETTINGS_ROUTE}
            target="_blank"
            variant="outlined"
            color="primary"
            endIcon={<OpenInNewIcon />}
          >
            {t("retentionConflictModal.organizationRetention")}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={onClose}
        >
          {t("common:close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  // Un poco más angosto que "sm" (600): el texto corta a ~65 caracteres
  paper: {
    maxWidth: 520,
  },
  subtitle: {
    fontWeight: 300,
  },
  message: {
    paddingTop: 0,
    paddingBottom: theme.spacing(1.5),
  },
  sectionLabel: {
    fontSize: "0.875rem",
    fontWeight: 500,
    letterSpacing: "0.02857em",
    textTransform: "uppercase",
  },
  list: {
    marginTop: theme.spacing(2),
    maxHeight: 260,
    overflowY: "auto",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  listItem: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: theme.spacing(0.5, 1.5),
    padding: theme.spacing(1.5, 0.5),
  },
  link: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    // Ocupa su celda del grid y deja que el nombre corte con ellipsis ahí
    minWidth: 0,
    maxWidth: "100%",
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  name: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  usage: {
    // Misma columna que el nombre: el ellipsis corta donde empieza el chip
    gridColumn: 1,
    // Permite que el ellipsis de noWrap actúe dentro de la celda del grid
    minWidth: 0,
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
    },
    "& a:hover": {
      textDecoration: "underline",
    },
  },

  // El ícono del título es el único rojo del modal: marca que es un error
  header: {
    position: "relative",
    paddingRight: theme.spacing(7),
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    padding: theme.spacing(0.5),
  },
  title: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
  },
  titleIcon: {
    color: theme.palette.error.main,
    marginTop: 3,
    flexShrink: 0,
  },
  strong: {
    fontWeight: 500,
  },
}));
